import { NavPage } from '@/components/breadcrumbs/Breadcrumbs';
import { Button } from '@/components/form/Button';
import { useModal } from '@/components/modal/useModal';
import SiUserPlus from '@/icons/SiUserPlus';
import SiUsersPlus from '@/icons/SiUsersPlus';
import BreadcrumbPageHeader from '@/layouts/BreadcrumbPageHeader';
import { PageLoading } from '@/layouts/PageLoading';
import { Container } from '@/layouts/main/Main';
import { Company } from '@/services/api/contracts/companies';
import { useCompanies, useGetPartnerHardware } from '@/services/api/hooks/companies';
import { usePartner } from '@/services/api/hooks/partners';
import { RoleType, useUser } from '@/services/authentication/hooks/user';
import { PageTitle } from '@/utils/Title';
import { FunctionComponent, Suspense } from 'react';
import { useParams } from 'react-router';
import BulkCreateCustomerModal from './BulkCreateCustomerModal';
import CreateCustomerModal from './CreateCustomerModal';
import CustomerCard from './CustomerCard';

const CustomerPageContent: FunctionComponent = () => {
  const { role } = useUser();
  const { partnerId } = useParams<'partnerId'>();

  if (!partnerId) {
    throw Error('Partner ID must be provided');
  }

  const [msp] = usePartner(partnerId);

  const breadcrumbs: NavPage[] = [
    {
      name: msp.name,
      to: `/msps/${partnerId}/companies/companiesList`
    },
    {
      name: 'Customers',
      to: `/msps/${partnerId}/companies/companiesList`
    }
  ];

  if (role.type === RoleType.Admin) {
    breadcrumbs.unshift({
      name: 'MSPs',
      to: '/msps/manageMsps'
    });
  }

  const [customers] = useCompanies(partnerId);
  const [hardware] = useGetPartnerHardware(partnerId);

  const createCustomerModalProps = useModal<{ mspId: string }>();
  const bulkCreateCustomerModalProps = useModal<{ mspId: string }>();

  return (
    <>
      <CreateCustomerModal {...createCustomerModalProps} />
      <BulkCreateCustomerModal {...bulkCreateCustomerModalProps} />
      <BreadcrumbPageHeader breadcrumbs={breadcrumbs}>
        <div className="flex justify-between gap-5 items-center font-bold">
          <h3 className="text-3xl">Customers</h3>
          <div className="flex gap-2">
            <Button className="h-fit" onClick={() => bulkCreateCustomerModalProps.open({ mspId: partnerId })}>
              <SiUsersPlus className="text-xl" />
              <span>Bulk upload customers</span>
            </Button>
            <Button onClick={() => createCustomerModalProps.open({ mspId: partnerId })} className="h-fit">
              <SiUserPlus className="text-xl" />
              <span>Create new customer for {msp.name}</span>
            </Button>
          </div>
        </div>
      </BreadcrumbPageHeader>
      <div className="flex-1 overflow-y-auto">
        <Container animate="pop" className="w-full py-8">
          <div className="grid h-fit grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-4">
            {customers.map((customer: Company) => (
              <CustomerCard
                customer={customer}
                hardware={hardware.items.find(e => e.company.id === customer.id)}
                key={customer.id}
              />
            ))}
          </div>
        </Container>
      </div>
    </>
  );
};

const CustomerPage: FunctionComponent = () => {
  return (
    <>
      <PageTitle>MSP customers</PageTitle>
      <Suspense fallback={<PageLoading />}>
        <CustomerPageContent />
      </Suspense>
    </>
  );
};

export default CustomerPage;
